import * as React from "react";
import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { CalcRequestType } from "../../../../../application/domain/payment/calc-request";
import { RefundRequest } from "../../../../../application/domain/payment/refund-request";
import { useModal } from "../../../../../utils/custom-hooks";
import { PaymentItemTable } from "../../../../organisms/smart-apply/payment/payment-detail/payment-item-table";
import { PaymentSummaryV2 } from "../../../../organisms/smart-apply/payment/common/payment-summary-v2";
import { RBACButton } from "../../../../molecules/rbac-button";
import { AddNewPayment } from "../payment-info/add-new-payment";
import { PaymentListV2 } from "./list";
import {
  PaymentDetailByPaymentJSON,
  PaymentSummaryJSON,
} from "../../../../../application/domain/payment/payment-summary";
import {
  gqlToDomainInvoiceType,
  InvoiceItemType,
  TaxRateLiterals,
} from "@graffer-inc/cloud-gov-core";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { matchType } from "../../../../../application/domain/base/base-type";
import { QualifiedInvoiceIssueRequest } from "../qualified-invoice/issue-request";

type PaymentInfoProps = {
  application: ApplicationEntity;
  procedure: ProcedureEntity;
  paymentSummary: GrafferGqlResult<PaymentSummaryJSON>;
  paymentDetailByPayments: GrafferGqlResult<PaymentDetailByPaymentJSON[]>;
  addPayment: (calcReq: CalcRequestType) => Promise<boolean>;
  withdrawPayment: (paymentIndex: number) => Promise<boolean>;
  refund: (
    index: number,
    req: RefundRequest,
    message?: string,
  ) => Promise<boolean>;
  adjustExceedingOrDeficiency: (index: number) => Promise<boolean>;
};

export const PaymentV2: React.FC<PaymentInfoProps> = ({
  application,
  paymentSummary,
  paymentDetailByPayments,
  procedure,
  addPayment,
  withdrawPayment,
  refund,
  adjustExceedingOrDeficiency,
}) => {
  const newPostPaymentModal = useModal();
  return (
    <div
      style={{
        maxWidth: "800px",
      }}
    >
      <>
        {paymentSummary.matchResult(
          {
            errorTypes: "",
          },
          {
            loading: null,
            unexpected: null,
            success: (paymentSummary) => {
              return (
                <>
                  <h2 className="title is-size-4">全ての支払いの合計</h2>
                  <div>
                    <PaymentItemTable
                      isShowDate={true}
                      invoice={{
                        items: paymentSummary.operations.reduce(
                          (pv: InvoiceItemType[], cv) => [
                            ...pv,
                            ...cv.items
                              .filter(
                                (v) =>
                                  !(
                                    cv.paymentTransactionType !== "CHARGE" &&
                                    v.amount === 0
                                  ),
                              )
                              .map((v, idx) => ({
                                type: gqlToDomainInvoiceType(v.type),
                                amount: matchType(cv.paymentTransactionType, {
                                  CHARGE: matchType(v.type, {
                                    Default: v.amount,
                                    Deficiency: -v.amount,
                                    DeficiencyAdjuster: v.amount,
                                    Exceeding: v.amount,
                                    ExceedingAdjuster: -v.amount,
                                  }),
                                  ADJUST_CHARGE: v.amount,
                                  ADJUST_REFUND: -v.amount,
                                  PARTIAL_REFUND: -v.amount,
                                  REFUND: -v.amount,
                                }),
                                taxRate: v.taxRate as TaxRateLiterals,
                                description: v.description,
                                createdAt: cv.createdAt,
                                operationIndex: idx,
                              })),
                          ],
                          [],
                        ),
                      }}
                    />
                    <div>
                      <PaymentSummaryV2 payment={paymentSummary} />
                    </div>
                  </div>
                </>
              );
            },
          },
        )}
        <div className="columns mt-6">
          <div className="column">
            <h2 className="title is-size-4">支払い一覧</h2>
          </div>
          <div className="column">
            <div className="has-text-right mb-3">
              <RBACButton
                target={procedure}
                requirePermissions={"applications.post_payment_add"}
                onClick={newPostPaymentModal.open}
                disabled={!application.isPaymentAddable()}
                buttonTypes={"secondary"}
                buttonText={"支払いを依頼する"}
              />
            </div>
          </div>
        </div>
        <PaymentListV2
          procedure={procedure}
          paymentDetailByPayments={paymentDetailByPayments}
          application={application}
          withdrawn={withdrawPayment}
          refund={refund}
          adjustExceedingOrDeficiency={adjustExceedingOrDeficiency}
        />
        <QualifiedInvoiceIssueRequest application={application} />
        {newPostPaymentModal.isActive && (
          <AddNewPayment
            onAddPayment={addPayment}
            onClose={newPostPaymentModal.close}
          />
        )}
      </>
    </div>
  );
};
